import { Controller } from "@hotwired/stimulus"

const OFFSET = 10 // px — matches React's sideOffset: 10
const EDGE_MARGIN = 8 // px — gutter kept between the tooltip and the viewport
const OPPOSITE = { top: "bottom", bottom: "top", left: "right", right: "left" }

// Connects to data-controller="ui--tooltip"
// Hotwire parity port of @a-safe-digital/react-tooltip. Handles show/hide with
// delay, keyboard support, controlled mode (open value) and viewport flipping.
//
// Touch (tap-to-open value, CONEK-9329): hover never fires on a phone and the
// focus a tap gives is not :focus-visible, so a plain tooltip cannot be read
// on touch at all. With tapToOpen a click on the trigger toggles the tooltip
// open immediately (no delay), and a pointerdown outside it or Escape closes it.
//
// While visible the tooltip element is PORTALED to <body> and positioned with
// `position: fixed` against the trigger's viewport rect. This mirrors Radix's
// portal: an absolutely-positioned tooltip only competes for z-index inside its
// nearest stacking context and is clipped by any ancestor `overflow: hidden/auto`
// (tables, cards, scrollers), so `z-tooltip` (90) alone can't keep it on top.
// Portaling to <body> escapes every ancestor stacking context and overflow.
export default class extends Controller {
  static targets = ["tooltip", "trigger", "content"]
  static values = {
    delay: { type: Number, default: 500 },
    open: { type: Boolean, default: false },
    position: { type: String, default: "bottom" },
    tapToOpen: { type: Boolean, default: false },
  }

  connect() {
    this.timeout = null
    this._portaled = false

    // Cache the tooltip/content nodes now: once portaled to <body> they leave
    // this controller's scope, so the Stimulus target getters stop finding them.
    this._tooltipEl = this.hasTooltipTarget ? this.tooltipTarget : null
    this._contentEl = this.hasContentTarget ? this.contentTarget : null

    this._tapOpen = false
    this._outsideHandler = this._handleOutside.bind(this)
    this._escapeHandler = this._handleEscape.bind(this)
    this._repositionHandler = this._reposition.bind(this)
    document.addEventListener("keydown", this._escapeHandler)

    // Controlled mode: reflect the initial open state immediately (no delay).
    if (this.openValue) this._reveal()
  }

  disconnect() {
    this.clearTimeout()
    document.removeEventListener("keydown", this._escapeHandler)
    document.removeEventListener("pointerdown", this._outsideHandler, true)
    this._stopTracking()
    // Drop the portaled node so it doesn't outlive the controller's element
    // (e.g. after a Turbo navigation).
    if (this._portaled && this._tooltipEl) this._tooltipEl.remove()
  }

  // Programmatic control: toggling data-ui--tooltip-open-value shows/hides
  // without hover (equivalent to React's `openTooltip` prop).
  openValueChanged() {
    if (!this._tooltipEl) return
    if (this.openValue) {
      this._reveal()
    } else {
      this._conceal()
    }
  }

  show(event) {
    // Keyboard focus only. Focus that comes from a click — or that a modal or
    // drawer hands back to its opener after being closed with the mouse — is
    // not :focus-visible, and opening there leaves the tooltip stuck open away
    // from the pointer. Radix does the same.
    if (event?.type === "focusin" && !event.target.matches?.(":focus-visible")) {
      return
    }

    this.clearTimeout()

    this.timeout = setTimeout(() => {
      this._reveal()
    }, this.delayValue)
  }

  hide(event) {
    // Focus moving to another focusable inside the same trigger is not a
    // blur of the trigger: hiding here would flash the tooltip off and back on
    // after the show delay.
    if (
      event?.type === "focusout" &&
      this.hasTriggerTarget &&
      this.triggerTarget.contains(event.relatedTarget)
    ) {
      return
    }

    this.clearTimeout()

    // In controlled mode the open value owns visibility — ignore hover-out.
    if (this.openValue) return

    this._conceal()
  }

  // Tap/click on the trigger (tapToOpen only). The first tap opens at once —
  // hover may already have scheduled a delayed show, so that timer is dropped —
  // and a second tap closes it. A click on a tooltip that hover already opened
  // keeps it open instead of closing it, so a mouse click never makes it
  // flicker shut; moving the mouse away still closes it (mouseleave), and on
  // touch, where there is no mouseleave, the outside tap does.
  toggle(event) {
    if (!this.tapToOpenValue || this.openValue) return

    event?.preventDefault()
    this.clearTimeout()

    if (this._tapOpen) {
      this._conceal()
      return
    }

    this._reveal()
    this._tapOpen = true
    document.addEventListener("pointerdown", this._outsideHandler, true)
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  // --- private ---

  _reveal() {
    if (!this._tooltipEl) return

    this._portal()
    this._resetFlip()
    this._tooltipEl.classList.remove("hidden")
    this._place()
    this._startTracking()

    this.element.dispatchEvent(
      new CustomEvent("tooltip:show", { bubbles: true }),
    )
  }

  _conceal() {
    if (!this._tooltipEl) return

    this._tapOpen = false
    document.removeEventListener("pointerdown", this._outsideHandler, true)
    this._tooltipEl.classList.add("hidden")
    this._stopTracking()
    this._unportal()

    this.element.dispatchEvent(
      new CustomEvent("tooltip:hide", { bubbles: true }),
    )
  }

  // Position against the trigger on the primary side, then flip to the opposite
  // side if it overflows the viewport.
  _place() {
    this._applyPosition(this.positionValue)

    const rect = this._tooltipEl.getBoundingClientRect()
    if (this._clips(rect, this.positionValue)) {
      this._applyPosition(OPPOSITE[this.positionValue])
      // Swap the entrance animation to match the flipped side. Class strings
      // come from data attributes rendered by the ViewComponent so no
      // design-system classes are hardcoded here.
      this._swapClasses(this._contentEl, "primaryAnimation", "flipAnimation")
      this._tooltipEl.dataset.flipped = "true"
    }
  }

  // Keep the (fixed) tooltip anchored to the trigger while the page scrolls or
  // resizes — fixed elements don't follow their scroll container.
  _reposition() {
    if (!this._tooltipEl || this._tooltipEl.classList.contains("hidden")) return

    const side =
      this._tooltipEl.dataset.flipped === "true"
        ? OPPOSITE[this.positionValue]
        : this.positionValue
    this._applyPosition(side)
  }

  // Fixed-position the (portaled) tooltip against the trigger. Inline styles
  // override the ERB's Tailwind absolute-positioning classes while shown; they
  // are cleared on _unportal so the no-JS/hidden state falls back to the classes.
  _applyPosition(side) {
    if (!this.hasTriggerTarget) return

    const tr = this.triggerTarget.getBoundingClientRect()
    let top, left, transform

    switch (side) {
      case "top":
        top = tr.top - OFFSET
        left = tr.left + tr.width / 2
        transform = "translate(-50%, -100%)"
        break
      case "left":
        top = tr.top + tr.height / 2
        left = tr.left - OFFSET
        transform = "translate(-100%, -50%)"
        break
      case "right":
        top = tr.top + tr.height / 2
        left = tr.right + OFFSET
        transform = "translate(0, -50%)"
        break
      case "bottom":
      default:
        top = tr.bottom + OFFSET
        left = tr.left + tr.width / 2
        transform = "translate(-50%, 0)"
        break
    }

    // Flipping only rescues the side the tooltip opens towards, so a top/bottom
    // tooltip whose trigger sits near a viewport edge is still clipped sideways
    // — it is centred on the trigger and half of it hangs off screen. Nudge the
    // centre back inside, the same guard the engage tooltip applies to its panel.
    if (side === "top" || side === "bottom") {
      left = this._clampCentre(left, this._tooltipEl.offsetWidth)
    }

    const s = this._tooltipEl.style
    s.position = "fixed"
    s.margin = "0"
    s.top = `${Math.round(top)}px`
    s.left = `${Math.round(left)}px`
    s.right = "auto"
    s.bottom = "auto"
    s.transform = transform
    // Tailwind v4's `-translate-x-1/2` (from the ERB position classes) emits the
    // standalone `translate` property, which composes with `transform` instead of
    // being overridden by it — leaving the tooltip shifted by a second -50%.
    // Neutralise it so only our centering transform applies.
    s.translate = "none"
  }

  // True when the tooltip overflows the viewport on the given side.
  _clips(rect, side) {
    const vw = window.innerWidth
    const vh = window.innerHeight

    if (side === "top") return rect.top < 0
    if (side === "bottom") return rect.bottom > vh
    if (side === "left") return rect.left < 0
    if (side === "right") return rect.right > vw
    return false
  }

  // Keep a centre-anchored tooltip on screen. `centre` is the trigger's midpoint
  // and the element is shifted -50%, so the centre may only travel to within
  // half a tooltip plus the gutter of each edge. A tooltip too wide to fit at
  // all is left centred in the viewport rather than pinned to one side.
  _clampCentre(centre, width) {
    const half = width / 2
    const min = half + EDGE_MARGIN
    const max = window.innerWidth - half - EDGE_MARGIN

    if (min > max) return window.innerWidth / 2

    return Math.min(Math.max(centre, min), max)
  }

  // Restore the primary-side animation class if a previous show had flipped.
  _resetFlip() {
    if (!this._tooltipEl || this._tooltipEl.dataset.flipped !== "true") return

    this._swapClasses(this._contentEl, "flipAnimation", "primaryAnimation")
    delete this._tooltipEl.dataset.flipped
  }

  _portal() {
    if (this._portaled || !this._tooltipEl) return

    this._originParent = this._tooltipEl.parentNode
    this._originNext = this._tooltipEl.nextSibling
    document.body.appendChild(this._tooltipEl)
    this._portaled = true
  }

  _unportal() {
    if (!this._portaled || !this._tooltipEl) return

    const s = this._tooltipEl.style
    s.position =
      s.top =
      s.left =
      s.right =
      s.bottom =
      s.margin =
      s.transform =
      s.translate =
        ""

    if (this._originParent) {
      this._originParent.insertBefore(this._tooltipEl, this._originNext)
    }
    this._portaled = false
  }

  _startTracking() {
    // Capture phase catches scrolling inside nested overflow containers too.
    window.addEventListener("scroll", this._repositionHandler, true)
    window.addEventListener("resize", this._repositionHandler)
  }

  _stopTracking() {
    window.removeEventListener("scroll", this._repositionHandler, true)
    window.removeEventListener("resize", this._repositionHandler)
  }

  _swapClasses(el, fromKey, toKey) {
    if (!el) return

    const from = el.dataset[fromKey]
    const to = el.dataset[toKey]
    if (from) el.classList.remove(...from.split(/\s+/).filter(Boolean))
    if (to) el.classList.add(...to.split(/\s+/).filter(Boolean))
  }

  // A pointerdown anywhere but the trigger or the (portaled) tooltip closes a
  // tap-opened tooltip. Capture phase, so a handler that stops propagation
  // further down cannot keep it stuck open.
  _handleOutside(event) {
    const target = event.target
    if (this.element.contains(target)) return
    if (this._tooltipEl && this._tooltipEl.contains(target)) return

    this._conceal()
  }

  _handleEscape(event) {
    if (
      event.key === "Escape" &&
      !this.openValue &&
      this._tooltipEl &&
      !this._tooltipEl.classList.contains("hidden")
    ) {
      this._conceal()
    }
  }
}
